import { components } from "@/api-schema";
import { RelocationOrderStatus } from "@/api-schema-enums";
import { getCoords } from "@/common/helpers/get-coords";
import { createRouteMapLayers } from "@/components/deck-map/_helpers/create-route-map-layers";
import { createMapRoute, createMapRouteData, type MapRoute } from "@/components/deck-map/_helpers/map-route";
import { MapThemePalette } from "@/components/deck-map/_helpers/map-theme";
import { useRouteAnimationFrame } from "@/components/deck-map/_hooks/use-route-animation-frame";
import { DeckMap } from "@/components/deck-map/deck-map";
import { EMPTY_VALUE_PLACEHOLDER } from "@/components/ui/empty-value";
import { MapViewState, ScatterplotLayer, type PickingInfo } from "deck.gl";
import { useCallback, useMemo, useState } from "react";
import { ACTIVE_STATUSES_SET, STATUS_CATEGORIES, StatusCategory } from "../map-statuses";
import { MapTooltip, TooltipInfo, TooltipType } from "./map-tooltip";

type Branch = components["schemas"]["Branch"];
type RelocationOrder = components["schemas"]["RelocationOrder"];

interface BranchPoint {
  uuid: string;
  name: string;
  latitude: number;
  longitude: number;
  incomingCount: number;
  outgoingCount: number;
}

interface TransitRoute extends MapRoute {
  uuid: string;
  sourceBranchName: string;
  currentBranchName: string | null;
  targetBranchName: string;
  vehicleVin: string;
  vehicleBrand: string;
  vehicleModelGeneration: string;
  status: RelocationOrder["status"];
  statusCategory: StatusCategory;
}

const BADEN_WUERTTEMBERG_CENTER: MapViewState = {
  longitude: 9.1,
  latitude: 48.55,
  zoom: 7.8,
  pitch: 45,
  bearing: -15,
};

interface TransitMapProps {
  branches: Branch[];
  relocationOrders: RelocationOrder[];
  palette: MapThemePalette;
  isKioskMode?: boolean;
}

export function TransitMap({ branches, relocationOrders, palette, isKioskMode }: TransitMapProps) {
  const [tooltip, setTooltip] = useState<TooltipInfo | null>(null);
  const animationFrame = useRouteAnimationFrame();

  const branchPoints = useMemo<BranchPoint[]>(() => {
    const branchCounts = new Map<string, { incoming: number; outgoing: number }>();

    for (const order of relocationOrders) {
      if (!ACTIVE_STATUSES_SET.has(order.status as RelocationOrderStatus)) continue;

      if (order.originBranch?.uuid) {
        const counts = branchCounts.get(order.originBranch.uuid) ?? { incoming: 0, outgoing: 0 };
        counts.outgoing++;
        branchCounts.set(order.originBranch.uuid, counts);
      }
      if (order.destinationBranch?.uuid) {
        const counts = branchCounts.get(order.destinationBranch.uuid) ?? { incoming: 0, outgoing: 0 };
        counts.incoming++;
        branchCounts.set(order.destinationBranch.uuid, counts);
      }
    }

    return branches
      .filter((b) => b.address?.latitude != null && b.address?.longitude != null)
      .map((b) => {
        const counts = branchCounts.get(b.uuid) ?? { incoming: 0, outgoing: 0 };
        return {
          uuid: b.uuid,
          name: b.name,
          latitude: b.address.latitude!,
          longitude: b.address.longitude!,
          incomingCount: counts.incoming,
          outgoingCount: counts.outgoing,
        };
      });
  }, [branches, relocationOrders]);

  const transitRoutes = useMemo<TransitRoute[]>(() => {
    const branchesByUuid = new Map(branches.map((branch) => [branch.uuid, branch]));

    const routes: TransitRoute[] = [];
    for (const order of relocationOrders) {
      if (!ACTIVE_STATUSES_SET.has(order.status as RelocationOrderStatus)) {
        continue;
      }
      const originBranch = branchesByUuid.get(order.originBranch?.uuid ?? "") ?? order.originBranch;
      const destinationBranch = branchesByUuid.get(order.destinationBranch?.uuid ?? "") ?? order.destinationBranch;
      const originCoords = getCoords(order.originAddress, originBranch);
      const currentCoords = getCoords(order.currentAddress, order.currentBranch);
      const destCoords = getCoords(order.destinationAddress, destinationBranch);
      if (originCoords == null || destCoords == null) {
        continue;
      }
      const statusCategory = STATUS_CATEGORIES[order.status] ?? StatusCategory.Issue;
      routes.push({
        ...createMapRoute(originCoords, destCoords, currentCoords),
        uuid: order.uuid,
        sourceBranchName: originBranch?.name ?? order.originAddress?.city ?? "External",
        currentBranchName: currentCoords ? (order.currentBranch?.name ?? order.currentAddress?.city ?? null) : null,
        targetBranchName: destinationBranch?.name ?? order.destinationAddress?.city ?? "External",
        vehicleVin: order.vehicle?.vin ?? EMPTY_VALUE_PLACEHOLDER,
        vehicleBrand: order.vehicle?.modelGeneration?.modelFamily?.brand?.name ?? "",
        vehicleModelGeneration: order.vehicle?.modelGeneration?.name ?? "",
        status: order.status,
        statusCategory,
      });
    }
    return routes;
  }, [branches, relocationOrders]);

  const routeData = useMemo(() => createMapRouteData(transitRoutes), [transitRoutes]);
  const activeBranchPoints = useMemo(
    () => branchPoints.filter((branch) => branch.incomingCount + branch.outgoingCount > 0),
    [branchPoints],
  );

  const handleHover = useCallback((info: PickingInfo<BranchPoint | TransitRoute>) => {
    if (!info.object) {
      setTooltip(null);
      return;
    }

    if (info.layer?.id === "branch-points") {
      const branch = info.object as BranchPoint;
      setTooltip({
        x: info.x,
        y: info.y,
        type: TooltipType.Branch,
        name: branch.name,
        incoming: branch.incomingCount,
        outgoing: branch.outgoingCount,
      });
    } else {
      const arc = info.object as TransitRoute;
      setTooltip({
        x: info.x,
        y: info.y,
        type: TooltipType.Arc,
        from: arc.sourceBranchName,
        current: arc.currentBranchName,
        to: arc.targetBranchName,
        vin: arc.vehicleVin,
        vehicle: `${arc.vehicleBrand} ${arc.vehicleModelGeneration}`.trim(),
        status: arc.status,
        statusCategory: arc.statusCategory,
      });
    }
  }, []);

  const layers = useMemo(
    () => [
      new ScatterplotLayer<BranchPoint>({
        id: "branch-glow",
        data: activeBranchPoints,
        getPosition: (d) => [d.longitude, d.latitude],
        getFillColor: palette.branch.glow,
        getRadius: (d) => (d.incomingCount + d.outgoingCount) * 300 + 800,
        filled: true,
        radiusMinPixels: 8,
        radiusMaxPixels: 40,
        pickable: false,
        parameters: {
          depthWriteEnabled: false,
        },
      }),
      createRouteMapLayers({
        data: routeData,
        animationFrame,
        palette,
        onHover: handleHover,
      }),
      new ScatterplotLayer<BranchPoint>({
        id: "branch-points",
        data: branchPoints,
        getPosition: (d) => [d.longitude, d.latitude],
        getFillColor: palette.branch.fill,
        getLineColor: palette.branch.stroke,
        getRadius: (d) => Math.max(400, (d.incomingCount + d.outgoingCount) * 120 + 400),
        lineWidthMinPixels: 2,
        stroked: true,
        filled: true,
        radiusMinPixels: 4,
        radiusMaxPixels: 20,
        pickable: true,
        onHover: handleHover,
        parameters: {
          depthWriteEnabled: false,
        },
      }),
    ],
    [activeBranchPoints, branchPoints, handleHover, palette, routeData, animationFrame],
  );

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-lg border"
      style={{
        backgroundColor: palette.surface.mapBackground,
        borderColor: palette.surface.border,
      }}
    >
      <DeckMap
        mapStyle={palette.basemapStyleUrl}
        initialViewState={BADEN_WUERTTEMBERG_CENTER}
        controller={!isKioskMode}
        layers={layers}
        getTooltip={() => null}
      />
      <MapTooltip info={tooltip} palette={palette} />
    </div>
  );
}
